import React, { useEffect, useState, useCallback } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  UserPlus,
  Trash2,
  ShieldCheck,
  Eye,
  Pencil,
  X,
  Wallet,
  TrendingDown,
  Target,
  FileText,
  Folder,
  Download,
  Share2,
  Inbox,
  Lock,
} from "lucide-react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { api, formatApiError, fmtINR } from "../lib/api";
import {
  assetTotal,
  assetInvested,
  hasQuantity,
  isPerUnit,
} from "../lib/assets";

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
const titleCase = (s) =>
  (s || "")
    .toString()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const bytesToSize = (bytes) => {
  const b = Number(bytes || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const initials = (name, email) =>
  (name || email || "?").trim().charAt(0).toUpperCase();

const QTY_UNIT = {
  stocks: "shares",
  gold: "g",
  silver: "g",
  crypto: "units",
  collectibles: "units",
};
// Only the categories that actually carry a quantity allow partial-by-unit.
const isQtyAsset = (a) => hasQuantity(a?.category) && Number(a?.quantity) > 0;
const assetUnit = (a) => QTY_UNIT[a?.category] || "units";

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : null;

// Categories that share a value list (asset / liability / goal).
const VALUE_CATEGORIES = [
  {
    key: "share_assets",
    idsKey: "asset_ids",
    catalog: "assets",
    label: "Assets",
    icon: Wallet,
    amount: (a) => a.value,
    sub: (a) => titleCase(a.category),
  },
  {
    key: "share_liabilities",
    idsKey: "liability_ids",
    catalog: "liabilities",
    label: "Liabilities",
    hint: "Loans, EMIs and balances",
    icon: TrendingDown,
    amount: (l) => l.outstanding,
    sub: (l) =>
      [
        titleCase(l.category),
        l.lender,
        l.emi ? `EMI ${fmtINR(l.emi, { compact: true })}` : null,
      ]
        .filter(Boolean)
        .join(" · "),
  },
  {
    key: "share_goals",
    idsKey: "goal_ids",
    catalog: "goals",
    label: "Goals",
    hint: "Targets and progress",
    icon: Target,
    amount: (g) => g.target_amount,
    sub: (g) =>
      [
        titleCase(g.category),
        g.target_date ? `by ${fmtDate(g.target_date)}` : null,
      ]
        .filter(Boolean)
        .join(" · "),
  },
];

const EMPTY_SHARE = {
  share_net_worth: false,
  share_assets: false,
  share_liabilities: false,
  share_goals: false,
  share_documents: false,
  asset_ids: null,
  liability_ids: null,
  goal_ids: null,
  document_ids: null,
  document_folder_ids: null,
  asset_shares: null,
};

// What a grant shares, as labelled chips.
function shareChips(grant) {
  const chips = [];
  if (grant.share_net_worth) chips.push({ label: "Net worth", icon: Wallet });
  for (const c of VALUE_CATEGORIES) {
    if (!grant[c.key]) continue;
    const ids = grant[c.idsKey];
    chips.push({
      label: `${c.label} · ${Array.isArray(ids) ? `${ids.length}` : "all"}`,
      icon: c.icon,
    });
  }
  if (grant.share_documents) {
    let detail = "all";
    if (Array.isArray(grant.document_ids))
      detail = `${grant.document_ids.length} files`;
    else if (Array.isArray(grant.document_folder_ids))
      detail = `${grant.document_folder_ids.length} folders`;
    chips.push({ label: `Documents · ${detail}`, icon: FileText });
  }
  return chips;
}

// ---------------------------------------------------------------------------
// page
// ---------------------------------------------------------------------------
export default function FamilySharing() {
  const [tab, setTab] = useState("outgoing");
  const [grants, setGrants] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [catalogs, setCatalogs] = useState({
    assets: [],
    liabilities: [],
    goals: [],
    documents: [],
    folders: [],
  });
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewingOwner, setViewingOwner] = useState(null);

  const load = useCallback(async () => {
    try {
      const [out, inc] = await Promise.all([
        api.get("/family/shares"),
        api.get("/family/shared-with-me"),
      ]);
      setGrants(out.data.grants || []);
      setIncoming(inc.data.shares || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const loadCatalogs = useCallback(async () => {
    const safe = (p) => p.then((r) => r.data).catch(() => ({}));
    const [a, l, g, d, f] = await Promise.all([
      safe(api.get("/assets")),
      safe(api.get("/liabilities")),
      safe(api.get("/goals")),
      safe(api.get("/vault/documents")),
      safe(api.get("/vault/folders")),
    ]);
    setCatalogs({
      assets: a.assets || [],
      liabilities: l.liabilities || [],
      goals: g.goals || [],
      documents: d.documents || [],
      folders: f.folders || [],
    });
  }, []);

  useEffect(() => {
    load();
    loadCatalogs();
  }, [load, loadCatalogs]);

  const revoke = async (id) => {
    if (!window.confirm("Revoke this share permanently?")) return;
    try {
      await api.delete(`/family/shares/${id}`);
      toast.success("Access revoked");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <DashboardLayout>
      <div className="mb-8" data-testid="family-sharing-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500">
              Trusted circle
            </p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
              Family Sharing
            </h1>
            <p className="mt-1 text-sm text-slate-500 max-w-2xl">
              Share a read-only window into your wealth — pick exactly what each
              person sees. Permissions are enforced on the server, not just
              hidden.
            </p>
          </div>
          <button
            onClick={() => setInviting(true)}
            className="btn-primary whitespace-nowrap"
            data-testid="open-invite"
          >
            <UserPlus className="h-4 w-4" /> Invite member
          </button>
        </div>

        {/* Tabs */}
        <div className="inline-flex rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-1 mb-6">
          {[
            {
              v: "outgoing",
              l: `I'm sharing (${grants.length})`,
              icon: Share2,
            },
            {
              v: "incoming",
              l: `Shared with me (${incoming.length})`,
              icon: Inbox,
            },
          ].map((t) => (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg transition ${
                tab === t.v
                  ? "bg-emerald-600 text-white"
                  : "text-slate-600 dark:text-slate-300"
              }`}
            >
              <t.icon className="h-3.5 w-3.5" /> {t.l}
            </button>
          ))}
        </div>

        {/* Outgoing */}
        {tab === "outgoing" &&
          (grants.length === 0 ? (
            <EmptyState
              icon={Share2}
              title="You're not sharing with anyone yet"
              desc="Invite a family member to give them a secure, read-only view of the parts of your finances you choose."
              cta="Invite member"
              onCta={() => setInviting(true)}
            />
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {grants.map((g) => (
                <GrantCard
                  key={g.id}
                  grant={g}
                  onEdit={() => setEditing({ ...EMPTY_SHARE, ...g })}
                  onRevoke={() => revoke(g.id)}
                />
              ))}
            </div>
          ))}

        {/* Incoming */}
        {tab === "incoming" &&
          (incoming.length === 0 ? (
            <EmptyState
              icon={Inbox}
              title="Nothing shared with you yet"
              desc="When someone shares their assets, goals or documents with you, they'll show up here — view-only."
            />
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {incoming.map((i) => (
                <IncomingCard
                  key={i.id}
                  share={i}
                  onView={() => setViewingOwner(i.owner_user_id)}
                />
              ))}
            </div>
          ))}
      </div>

      {/* Invite */}
      {inviting && (
        <ShareModal
          title="Invite family member"
          catalogs={catalogs}
          onClose={() => setInviting(false)}
          onSaved={() => {
            setInviting(false);
            load();
          }}
        />
      )}

      {/* Edit */}
      {editing && (
        <ShareModal
          title={`Edit access · ${editing.grantee_name || editing.grantee_email}`}
          catalogs={catalogs}
          grant={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      {/* Recipient viewer */}
      {viewingOwner && (
        <SnapshotModal
          ownerId={viewingOwner}
          onClose={() => setViewingOwner(null)}
        />
      )}
    </DashboardLayout>
  );
}

// ---------------------------------------------------------------------------
// cards
// ---------------------------------------------------------------------------
function Avatar({ name, email, size = "h-11 w-11", text = "text-base" }) {
  return (
    <div
      className={`grid ${size} place-items-center rounded-full bg-gradient-to-br from-emerald-500 to-blue-600 text-white font-display font-bold ${text} shrink-0`}
    >
      {initials(name, email)}
    </div>
  );
}

function Chip({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 px-2.5 py-1 text-[11px] font-medium">
      {Icon && <Icon className="h-3 w-3" />} {label}
    </span>
  );
}

function GrantCard({ grant, onEdit, onRevoke }) {
  const chips = shareChips(grant);
  const accepted = grant.invite_status === "accepted";
  return (
    <div className="card-soft p-5" data-testid={`grant-${grant.id}`}>
      <div className="flex items-start gap-3">
        <Avatar name={grant.grantee_name} email={grant.grantee_email} />
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-slate-900 dark:text-white truncate">
            {grant.grantee_name || grant.grantee_email}
          </div>
          <div className="text-xs text-slate-500 truncate">
            {grant.grantee_email}
          </div>
          <div className="mt-1 flex items-center gap-2 text-[11px]">
            {grant.relation && (
              <span className="text-slate-400">
                {titleCase(grant.relation)}
              </span>
            )}
            <span
              className={`inline-flex items-center gap-1 font-semibold ${
                accepted ? "text-emerald-600" : "text-amber-600"
              }`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  accepted ? "bg-emerald-500" : "bg-amber-500"
                }`}
              />
              {accepted ? "Active" : "Pending invite"}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5 min-h-[26px]">
        {chips.length === 0 ? (
          <span className="text-xs text-slate-400">Nothing shared yet</span>
        ) : (
          chips.map((c) => <Chip key={c.label} icon={c.icon} label={c.label} />)
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center gap-2">
        <button
          onClick={onEdit}
          data-testid={`edit-grant-${grant.id}`}
          className="btn-secondary !text-xs flex-1"
        >
          <Pencil className="h-3.5 w-3.5" /> Edit access
        </button>
        <button
          onClick={onRevoke}
          className="inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10"
        >
          <Trash2 className="h-3.5 w-3.5" /> Revoke
        </button>
      </div>
    </div>
  );
}

function IncomingCard({ share, onView }) {
  const chips = shareChips(share);
  return (
    <div className="card-soft p-5" data-testid={`incoming-${share.id}`}>
      <div className="flex items-start gap-3">
        <Avatar name={share.owner_name} email={share.owner_email} />
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-slate-900 dark:text-white truncate">
            {share.owner_name || share.owner_email}
          </div>
          <div className="text-xs text-slate-500 truncate">
            {share.owner_email}
          </div>
          <div className="mt-1 text-[11px] text-slate-400">shared with you</div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5 min-h-[26px]">
        {chips.map((c) => (
          <Chip key={c.label} icon={c.icon} label={c.label} />
        ))}
      </div>

      <button
        onClick={onView}
        data-testid={`view-${share.id}`}
        className="btn-primary w-full mt-4 !text-xs"
      >
        <Eye className="h-3.5 w-3.5" /> View shared data
      </button>
    </div>
  );
}

function EmptyState({ icon: Icon, title, desc, cta, onCta }) {
  return (
    <div className="card-soft p-12 text-center">
      <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-400">
        <Icon className="h-7 w-7" />
      </div>
      <h3 className="mt-4 font-display text-lg font-bold text-slate-900 dark:text-white">
        {title}
      </h3>
      <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">{desc}</p>
      {cta && (
        <button onClick={onCta} className="btn-primary mt-5 !text-xs">
          <UserPlus className="h-4 w-4" /> {cta}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// share modal (invite + edit) with the access editor
// ---------------------------------------------------------------------------
function ShareModal({ title, catalogs, grant, onClose, onSaved }) {
  const isEdit = !!grant;
  const [saving, setSaving] = useState(false);
  const [grantee, setGrantee] = useState({
    grantee_email: grant?.grantee_email || "",
    grantee_name: grant?.grantee_name || "",
    relation: grant?.relation || "",
  });
  const [share, setShare] = useState({ ...EMPTY_SHARE, ...(grant || {}) });

  const save = async () => {
    if (!isEdit && !grantee.grantee_email) {
      toast.error("Email is required");
      return;
    }
    setSaving(true);
    try {
      const body = {
        share_net_worth: share.share_net_worth,
        share_assets: share.share_assets,
        share_liabilities: share.share_liabilities,
        share_goals: share.share_goals,
        share_documents: share.share_documents,
        asset_ids: share.asset_ids,
        liability_ids: share.liability_ids,
        goal_ids: share.goal_ids,
        document_ids: share.document_ids,
        document_folder_ids: share.document_folder_ids,
        asset_shares: share.asset_shares,
      };
      if (isEdit) {
        await api.put(`/family/shares/${grant.id}`, body);
        toast.success("Access updated");
      } else {
        await api.post("/family/shares", { ...grantee, ...body });
        toast.success("Invitation sent");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title={title} onClose={onClose}>
      <div className="space-y-5">
        {!isEdit && (
          <div className="grid sm:grid-cols-3 gap-3" data-testid="grant-form">
            <Field label="Email" required>
              <input
                type="email"
                value={grantee.grantee_email}
                onChange={(e) =>
                  setGrantee({ ...grantee, grantee_email: e.target.value })
                }
                className="input-soft"
                placeholder="name@email.com"
                data-testid="grantee-email"
              />
            </Field>
            <Field label="Name">
              <input
                value={grantee.grantee_name}
                onChange={(e) =>
                  setGrantee({ ...grantee, grantee_name: e.target.value })
                }
                className="input-soft"
                placeholder="Optional"
                data-testid="grantee-name"
              />
            </Field>
            <Field label="Relation">
              <input
                value={grantee.relation}
                onChange={(e) =>
                  setGrantee({ ...grantee, relation: e.target.value })
                }
                className="input-soft"
                placeholder="e.g. Spouse"
                data-testid="grantee-relation"
              />
            </Field>
          </div>
        )}

        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
            What can they see?
          </div>
          <AccessEditor value={share} onChange={setShare} catalogs={catalogs} />
        </div>
      </div>

      <div className="flex gap-2 pt-5 mt-2 border-t border-slate-200 dark:border-slate-800">
        <button onClick={onClose} className="btn-secondary flex-1">
          Cancel
        </button>
        <button
          onClick={save}
          disabled={saving}
          className="btn-primary flex-1"
          data-testid={isEdit ? "save-grant-edit-btn" : "submit-grant-btn"}
        >
          {saving ? "Saving…" : isEdit ? "Save access" : "Send invite"}
        </button>
      </div>
    </ModalShell>
  );
}

function AccessEditor({ value, onChange, catalogs }) {
  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-3">
      {/* Net worth — total only */}
      <ToggleRow
        icon={ShieldCheck}
        label="Net Worth Total"
        hint="The single aggregate figure — no individual items."
        checked={!!value.share_net_worth}
        onChange={(c) => set({ share_net_worth: c })}
        testid="perm-share_net_worth"
      />

      <AssetShareRow value={value} set={set} assets={catalogs.assets || []} />

      {VALUE_CATEGORIES.filter((c) => c.catalog !== "assets").map((cat) => (
        <ValueCategoryRow
          key={cat.key}
          cat={cat}
          value={value}
          set={set}
          items={catalogs[cat.catalog] || []}
        />
      ))}

      <DocumentRow
        value={value}
        set={set}
        documents={catalogs.documents || []}
        folders={catalogs.folders || []}
      />
    </div>
  );
}

function Switch({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 ${
        checked ? "bg-emerald-600" : "bg-slate-300 dark:bg-slate-700"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-[22px]" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

function ToggleRow({
  icon: Icon,
  label,
  hint,
  checked,
  onChange,
  testid,
  children,
}) {
  return (
    <div
      className={`rounded-2xl border transition-all ${
        checked
          ? "border-emerald-300 dark:border-emerald-500/40 bg-emerald-50/40 dark:bg-emerald-500/[0.06] shadow-sm"
          : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
      }`}
    >
      <div className="flex items-center gap-3 p-3.5">
        <div
          className={`grid h-10 w-10 place-items-center rounded-xl shrink-0 transition-colors ${
            checked
              ? "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600"
              : "bg-slate-100 dark:bg-slate-800 text-slate-400"
          }`}
        >
          {Icon && <Icon className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {label}
          </div>
          {hint && (
            <div className="text-[11px] text-slate-400 mt-0.5">{hint}</div>
          )}
        </div>
        {/* hidden input keeps stable test hooks; the Switch is the control */}
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only"
          data-testid={testid}
        />
        <Switch checked={checked} onChange={onChange} />
      </div>
      {checked && children && (
        <div className="px-3.5 pb-3.5 border-t border-emerald-100/70 dark:border-emerald-500/10 pt-3">
          {children}
        </div>
      )}
    </div>
  );
}

function ScopeSwitch({ options, value, onChange }) {
  return (
    <div className="inline-flex rounded-lg bg-slate-100 dark:bg-slate-800 p-0.5 text-xs">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`px-2.5 py-1 rounded-md font-semibold transition ${
            value === o.value
              ? "bg-emerald-600 text-white"
              : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function AssetShareRow({ value, set, assets }) {
  const on = !!value.share_assets;
  const ids = value.asset_ids;
  const selectMode = Array.isArray(ids);
  const selected = new Set((ids || []).map(String));
  const shares = value.asset_shares || {};

  const compactShares = (obj) => (obj && Object.keys(obj).length ? obj : null);

  const toggleItem = (a) => {
    const sid = String(a.id);
    const next = new Set(selected);
    const nextShares = { ...shares };
    if (next.has(sid)) {
      next.delete(sid);
      delete nextShares[sid]; // dropping the asset drops any partial too
    } else {
      next.add(sid);
    }
    set({
      asset_ids: Array.from(next),
      asset_shares: compactShares(nextShares),
    });
  };

  const setPartial = (a, raw) => {
    const sid = String(a.id);
    const total = isQtyAsset(a) ? Number(a.quantity) : Number(a.value);
    const nextShares = { ...shares };
    const num = Number(String(raw).replace(/,/g, ""));
    // Empty / full / invalid -> share the whole asset (remove override).
    if (raw === "" || !Number.isFinite(num) || num <= 0 || num >= total) {
      delete nextShares[sid];
    } else {
      nextShares[sid] = num;
    }
    set({ asset_shares: compactShares(nextShares) });
  };

  return (
    <ToggleRow
      icon={Wallet}
      label="Assets"
      hint="Holdings, value, institution & returns"
      checked={on}
      onChange={(c) =>
        set({
          share_assets: c,
          asset_ids: c ? (value.asset_ids ?? null) : null,
          asset_shares: c ? value.asset_shares : null,
        })
      }
      testid="perm-share_assets"
    >
      <div>
        <div className="flex items-center gap-2 mb-2">
          <ScopeSwitch
            value={selectMode ? "selected" : "all"}
            onChange={(m) =>
              set({
                asset_ids: m === "all" ? null : [],
                asset_shares: m === "all" ? null : value.asset_shares,
              })
            }
            options={[
              { value: "all", label: "All" },
              { value: "selected", label: "Choose" },
            ]}
          />
          {selectMode && (
            <span className="text-[11px] text-slate-400">
              {selected.size} selected
            </span>
          )}
        </div>

        {selectMode &&
          (assets.length === 0 ? (
            <div className="text-xs text-slate-400 py-2">
              No assets to share yet.
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-100 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
              {assets.map((a) => {
                const checked = selected.has(String(a.id));
                const qty = isQtyAsset(a);
                const total = qty ? Number(a.quantity) : Number(a.value);
                const unit = qty ? assetUnit(a) : "₹";
                const partialVal = shares[String(a.id)];
                return (
                  <div key={a.id} className="px-3 py-2">
                    <label className="flex items-center gap-2.5 text-xs cursor-pointer">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleItem(a)}
                        className="shrink-0"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center font-medium text-slate-700 dark:text-slate-200">
                          <span className="truncate">{a.name}</span>
                          <span className="ml-auto pl-3 num-mono text-slate-400">
                            {fmtINR(assetTotal(a), { compact: true })}
                          </span>
                        </span>
                        <span className="block text-[11px] text-slate-400 truncate">
                          {[
                            titleCase(a.category),
                            a.institution,
                            qty ? `${a.quantity} ${unit}` : null,
                            a.expected_return
                              ? `${a.expected_return}% p.a.`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </span>
                    </label>

                    {checked && total > 0 && (
                      <div className="mt-2 ml-7 flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] text-slate-400">
                          Share only
                        </span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={partialVal ?? ""}
                          placeholder={qty ? `all (${total})` : "all"}
                          onChange={(e) => setPartial(a, e.target.value)}
                          className="input-soft !py-1 !text-xs w-24"
                        />
                        <span className="text-[11px] text-slate-400">
                          {qty
                            ? `${unit} of ${total} ${unit}`
                            : `₹ of ${fmtINR(total, { compact: true })}`}
                        </span>
                        {partialVal != null && (
                          <span className="text-[10px] font-semibold text-emerald-600">
                            partial
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
      </div>
    </ToggleRow>
  );
}

function ValueCategoryRow({ cat, value, set, items }) {
  const on = !!value[cat.key];
  const ids = value[cat.idsKey];
  const selectMode = Array.isArray(ids);
  const selected = new Set((ids || []).map(String));

  const toggleItem = (id) => {
    const next = new Set(selected);
    const sid = String(id);
    next.has(sid) ? next.delete(sid) : next.add(sid);
    set({ [cat.idsKey]: Array.from(next) });
  };

  return (
    <ToggleRow
      icon={cat.icon}
      label={cat.label}
      hint={cat.hint}
      checked={on}
      onChange={(c) =>
        set({
          [cat.key]: c,
          [cat.idsKey]: c ? (value[cat.idsKey] ?? null) : null,
        })
      }
      testid={`perm-${cat.key}`}
    >
      <div>
        <div className="flex items-center gap-2 mb-2">
          <ScopeSwitch
            value={selectMode ? "selected" : "all"}
            onChange={(m) => set({ [cat.idsKey]: m === "all" ? null : [] })}
            options={[
              { value: "all", label: "All" },
              { value: "selected", label: "Choose" },
            ]}
          />
          {selectMode && (
            <span className="text-[11px] text-slate-400">
              {selected.size} selected
            </span>
          )}
        </div>
        {selectMode && (
          <Checklist
            items={items}
            isChecked={(it) => selected.has(String(it.id))}
            onToggle={(it) => toggleItem(it.id)}
            renderLabel={(it) => (
              <>
                <span className="truncate">{it.name}</span>
                <span className="ml-auto pl-3 text-slate-400 num-mono">
                  {fmtINR(cat.amount(it), { compact: true })}
                </span>
              </>
            )}
            renderSub={(it) => cat.sub(it)}
            emptyLabel={`No ${cat.label.toLowerCase()} to share yet.`}
          />
        )}
      </div>
    </ToggleRow>
  );
}

function DocumentRow({ value, set, documents, folders }) {
  const on = !!value.share_documents;
  const folderName = (id) =>
    folders.find((f) => String(f.id) === String(id))?.name || "Unfiled";

  // Derive the active mode from which id list is set.
  const mode = Array.isArray(value.document_ids)
    ? "files"
    : Array.isArray(value.document_folder_ids)
      ? "folders"
      : "all";

  const setMode = (m) => {
    if (m === "all") set({ document_ids: null, document_folder_ids: null });
    else if (m === "folders")
      set({ document_folder_ids: [], document_ids: null });
    else set({ document_ids: [], document_folder_ids: null });
  };

  const fileIds = new Set((value.document_ids || []).map(String));
  const folderIds = new Set((value.document_folder_ids || []).map(String));

  const toggleFile = (id) => {
    const next = new Set(fileIds);
    const sid = String(id);
    next.has(sid) ? next.delete(sid) : next.add(sid);
    set({ document_ids: Array.from(next) });
  };
  const toggleFolder = (id) => {
    const next = new Set(folderIds);
    const sid = String(id);
    next.has(sid) ? next.delete(sid) : next.add(sid);
    set({ document_folder_ids: Array.from(next) });
  };

  return (
    <ToggleRow
      icon={FileText}
      label="Documents (Vault)"
      hint="All, by folder, or specific files"
      checked={on}
      onChange={(c) => set({ share_documents: c })}
      testid="perm-share_documents"
    >
      <div>
        <div className="flex items-center gap-2 mb-2">
          <ScopeSwitch
            value={mode}
            onChange={setMode}
            options={[
              { value: "all", label: "All" },
              { value: "folders", label: "By folder" },
              { value: "files", label: "Specific files" },
            ]}
          />
        </div>

        {mode === "folders" && (
          <Checklist
            items={folders}
            isChecked={(f) => folderIds.has(String(f.id))}
            onToggle={(f) => toggleFolder(f.id)}
            renderLabel={(f) => <span className="truncate">{f.name}</span>}
            renderSub={(f) =>
              `${documents.filter((d) => String(d.folder_id) === String(f.id)).length} files`
            }
            icon={Folder}
            emptyLabel="No folders yet — create folders in the Vault."
          />
        )}

        {mode === "files" && (
          <Checklist
            items={documents}
            isChecked={(d) => fileIds.has(String(d.id))}
            onToggle={(d) => toggleFile(d.id)}
            renderLabel={(d) => <span className="truncate">{d.title}</span>}
            renderSub={(d) =>
              `${folderName(d.folder_id)} · ${bytesToSize(d.size_bytes)}`
            }
            icon={FileText}
            emptyLabel="No documents uploaded yet."
          />
        )}
      </div>
    </ToggleRow>
  );
}

function Checklist({
  items,
  isChecked,
  onToggle,
  renderLabel,
  renderSub,
  emptyLabel,
  icon: Icon,
}) {
  if (!items.length)
    return <div className="text-xs text-slate-400 py-2">{emptyLabel}</div>;
  return (
    <div className="max-h-56 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
      {items.map((it) => {
        const checked = isChecked(it);
        return (
          <label
            key={it.id}
            className={`flex items-center gap-2.5 px-3 py-2.5 text-xs cursor-pointer transition-colors ${
              checked
                ? "bg-emerald-50/70 dark:bg-emerald-500/10"
                : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
            }`}
          >
            <Checkbox
              checked={checked}
              onCheckedChange={() => onToggle(it)}
              className="shrink-0"
            />
            {Icon && (
              <Icon
                className={`h-3.5 w-3.5 shrink-0 ${checked ? "text-emerald-600" : "text-slate-400"}`}
              />
            )}
            <span className="min-w-0 flex-1">
              <span className="flex items-center text-slate-700 dark:text-slate-200 font-medium">
                {renderLabel(it)}
              </span>
              {renderSub && (
                <span className="block text-[11px] text-slate-400 truncate">
                  {renderSub(it)}
                </span>
              )}
            </span>
          </label>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// recipient snapshot viewer (read-only + downloads)
// ---------------------------------------------------------------------------
function SnapshotModal({ ownerId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [snap, setSnap] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/family/shared/${ownerId}/snapshot`);
        setSnap(data);
      } catch (e) {
        toast.error(formatApiError(e));
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [ownerId, onClose]);

  const download = async (doc) => {
    try {
      const res = await api.get(`/vault/documents/${doc.id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], {
          type: doc.mime_type || "application/octet-stream",
        }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.title || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("You're not allowed to download this file.");
    }
  };

  const folderName = (id) =>
    (snap?.folders || []).find((f) => String(f.id) === String(id))?.name ||
    "Unfiled";

  return (
    <ModalShell
      title={
        loading
          ? "Loading…"
          : `${snap?.owner_name || "Shared"} · shared with you`
      }
      onClose={onClose}
      wide
      testid="snapshot-view"
    >
      {loading ? (
        <div className="py-16 text-center text-sm text-slate-500">Loading…</div>
      ) : !snap ? null : (
        <div className="space-y-6">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 px-3 py-1 text-[11px] font-medium">
            <Lock className="h-3 w-3" /> Read-only — you can view and download,
            but not edit.
          </div>

          {/* Net worth tiles */}
          {snap.total_assets !== undefined && (
            <div className="grid grid-cols-3 gap-3">
              <SummaryTile
                label="Assets"
                value={fmtINR(snap.total_assets, { compact: true })}
                accent="text-emerald-600"
              />
              <SummaryTile
                label="Liabilities"
                value={fmtINR(snap.total_liabilities || 0, { compact: true })}
                accent="text-rose-600"
              />
              <SummaryTile
                label="Net Worth"
                value={fmtINR(snap.net_worth || 0, { compact: true })}
              />
            </div>
          )}

          {/* Assets */}
          {snap.assets && (
            <SnapSection
              icon={Wallet}
              title="Assets"
              count={snap.assets.length}
            >
              {snap.assets.map((a) => (
                <AssetDetailCard key={a.id} a={a} />
              ))}
            </SnapSection>
          )}

          {/* Liabilities */}
          {snap.liabilities && (
            <SnapSection
              icon={TrendingDown}
              title="Liabilities"
              count={snap.liabilities.length}
            >
              {snap.liabilities.map((l) => (
                <LiabilityDetailCard key={l.id} l={l} />
              ))}
            </SnapSection>
          )}

          {/* Goals */}
          {snap.goals && (
            <SnapSection icon={Target} title="Goals" count={snap.goals.length}>
              {snap.goals.map((g) => (
                <GoalDetailCard key={g.id} g={g} />
              ))}
            </SnapSection>
          )}

          {/* Documents grouped by folder */}
          {snap.documents && (
            <SnapSection
              icon={FileText}
              title="Documents"
              count={snap.documents.length}
            >
              {snap.documents.length === 0 ? (
                <div className="px-4 py-3 text-xs text-slate-400">
                  No documents in this share.
                </div>
              ) : (
                Object.entries(
                  snap.documents.reduce((acc, d) => {
                    const k = folderName(d.folder_id);
                    (acc[k] = acc[k] || []).push(d);
                    return acc;
                  }, {}),
                ).map(([folder, docs]) => (
                  <div
                    key={folder}
                    className="border-t border-slate-100 dark:border-slate-800 first:border-t-0"
                  >
                    <div className="flex items-center gap-1.5 px-4 pt-3 pb-1 text-[11px] uppercase tracking-wider text-slate-400">
                      <Folder className="h-3 w-3" /> {folder}
                    </div>
                    {docs.map((d) => (
                      <div
                        key={d.id}
                        className="flex items-center gap-3 px-4 py-2.5"
                      >
                        <div className="grid h-9 w-9 place-items-center rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500 shrink-0">
                          <FileText className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
                            {d.title}
                          </div>
                          <div className="text-[11px] text-slate-400">
                            {titleCase(d.category) || "Document"} ·{" "}
                            {bytesToSize(d.size_bytes)}
                          </div>
                        </div>
                        <button
                          onClick={() => download(d)}
                          className="btn-secondary !text-xs shrink-0"
                        >
                          <Download className="h-3.5 w-3.5" /> Download
                        </button>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </SnapSection>
          )}

          {!snap.assets &&
            !snap.liabilities &&
            !snap.goals &&
            !snap.documents &&
            snap.total_assets === undefined && (
              <div className="py-10 text-center text-sm text-slate-500">
                This person hasn't shared any details with you.
              </div>
            )}
        </div>
      )}
    </ModalShell>
  );
}

function SummaryTile({
  label,
  value,
  accent = "text-slate-900 dark:text-white",
}) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className={`num-mono text-xl font-bold mt-1 ${accent}`}>{value}</div>
    </div>
  );
}

function SnapSection({ icon: Icon, title, count, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-emerald-600" />
        <h3 className="text-sm font-bold text-slate-900 dark:text-white">
          {title}
        </h3>
        <span className="text-xs text-slate-400">({count})</span>
      </div>
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        {children}
      </div>
    </div>
  );
}

function ItemCard({ children }) {
  return (
    <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-800 first:border-t-0">
      {children}
    </div>
  );
}

function CardHead({ name, value, valueLabel, valueAccent, partial }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 font-semibold text-sm text-slate-800 dark:text-slate-100 flex items-center gap-2">
        <span className="truncate">{name}</span>
        {partial && (
          <span className="shrink-0 rounded-full bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide">
            Partial
          </span>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className={`num-mono text-sm font-bold ${valueAccent}`}>
          {value}
        </div>
        <div className="text-[10px] text-slate-400">{valueLabel}</div>
      </div>
    </div>
  );
}

function DetailGrid({ fields }) {
  const rows = fields.filter(
    (f) => f && f.value !== null && f.value !== undefined && f.value !== "",
  );
  if (!rows.length) return null;
  return (
    <div className="mt-2.5 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
      {rows.map((f) => (
        <div key={f.label}>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">
            {f.label}
          </div>
          <div className="text-xs font-medium text-slate-700 dark:text-slate-200 break-words">
            {f.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function AssetDetailCard({ a }) {
  const qty = isQtyAsset(a);
  return (
    <ItemCard>
      <CardHead
        name={a.name}
        value={fmtINR(assetTotal(a))}
        valueLabel="Current value"
        valueAccent="text-slate-900 dark:text-white"
        partial={a.shared_partial}
      />
      <DetailGrid
        fields={[
          { label: "Type", value: titleCase(a.category) },
          {
            label: "Holding",
            value: qty
              ? isPerUnit(a.category)
                ? `${a.quantity} ${assetUnit(a)} @ ${fmtINR(a.value)}`
                : `${a.quantity} ${assetUnit(a)}`
              : null,
          },
          { label: "Institution", value: a.institution },
          {
            label: "Invested",
            value: assetInvested(a) ? fmtINR(assetInvested(a)) : null,
          },
          { label: "Purchased", value: fmtDate(a.purchase_date) },
          { label: "Matures", value: fmtDate(a.maturity_date) },
          {
            label: "Expected return",
            value: a.expected_return ? `${a.expected_return}% p.a.` : null,
          },
          { label: "Notes", value: a.notes },
        ]}
      />
    </ItemCard>
  );
}

function LiabilityDetailCard({ l }) {
  return (
    <ItemCard>
      <CardHead
        name={l.name}
        value={fmtINR(l.outstanding)}
        valueLabel="Outstanding"
        valueAccent="text-rose-600"
        partial={false}
      />
      <DetailGrid
        fields={[
          { label: "Type", value: titleCase(l.category) },
          { label: "Lender", value: l.lender },
          {
            label: "Original",
            value: l.original_amount ? fmtINR(l.original_amount) : null,
          },
          {
            label: "Interest",
            value: l.interest_rate ? `${l.interest_rate}% p.a.` : null,
          },
          {
            label: "EMI",
            value: l.emi
              ? `${fmtINR(l.emi)}${l.emi_date ? ` · ${l.emi_date}th` : ""}`
              : null,
          },
          { label: "Started", value: fmtDate(l.start_date) },
          { label: "Ends", value: fmtDate(l.end_date) },
          { label: "Notes", value: l.notes },
        ]}
      />
    </ItemCard>
  );
}

function GoalDetailCard({ g }) {
  const pct =
    Number(g.target_amount) > 0
      ? Math.min(
          100,
          (Number(g.current_amount || 0) / Number(g.target_amount)) * 100,
        )
      : 0;
  return (
    <ItemCard>
      <CardHead
        name={g.name}
        value={fmtINR(g.target_amount)}
        valueLabel="Target"
        valueAccent="text-slate-900 dark:text-white"
        partial={false}
      />
      <div className="mt-2 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
        <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-slate-400">
        <span>
          {fmtINR(g.current_amount || 0, { compact: true })} saved ·{" "}
          {pct.toFixed(0)}%
        </span>
        <span>
          {fmtDate(g.target_date) ? `by ${fmtDate(g.target_date)}` : ""}
        </span>
      </div>
      <DetailGrid
        fields={[
          { label: "Category", value: titleCase(g.category) },
          {
            label: "Inflation",
            value: g.inflation_rate ? `${g.inflation_rate}%` : null,
          },
          { label: "Notes", value: g.notes },
        ]}
      />
    </ItemCard>
  );
}

// ---------------------------------------------------------------------------
// shared bits
// ---------------------------------------------------------------------------
function ModalShell({ title, onClose, children, wide, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4">
      <div
        data-testid={testid}
        className={`w-full ${wide ? "max-w-3xl" : "max-w-2xl"} max-h-[90vh] overflow-hidden card-soft flex flex-col`}
      >
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-800">
          <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white truncate pr-3">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="grid h-8 w-8 place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </label>
      {children}
    </div>
  );
}
